import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import "./styles/Navigation.css";
import { Mark } from "../brand/Brand";

const NAV_LINKS = [
  { id: "home", label: "Index", to: "/" },
  { id: "work", label: "Work", to: "/work" },
  { id: "blog", label: "Blog", to: "/blog" },
];

const Navigation = () => {
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const { user, profile, logout } = useAuth();
  const dashboardPath = profile?.isAdmin ? "/admin" : "/dashboard";
  const authLinkStyle = {
    padding: "8px 14px",
    textDecoration: "none",
    fontFamily: "var(--fw-mono)",
    fontSize: 11,
    letterSpacing: "1.4px",
    textTransform: "uppercase",
    color: "var(--fw-muted)",
    background: "none",
    border: "none",
    cursor: "pointer",
  };

  const isActive = (link) => {
    if (link.to === "/") return location.pathname === "/";
    if (link.to === "/work") return location.pathname.startsWith("/work");
    if (link.to === "/blog") return location.pathname.startsWith("/blog");
    return false;
  };

  return (
    <nav
      style={{
        background: "var(--fw-cream)",
        borderBottom: "1px solid var(--fw-line)",
        position: "sticky",
        top: 0,
        zIndex: 50,
        backdropFilter: "blur(8px)",
      }}
    >
      <div
        style={{
          maxWidth: 1440,
          margin: "0 auto",
          padding: "18px 40px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        {/* Logo */}
        <Link
          to="/"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            textDecoration: "none",
            color: "var(--fw-ink)",
          }}
        >
          <Mark size={48} />
        </Link>

        {/* Desktop nav links */}
        <div className="fw-nav-links" style={{ display: "flex", gap: 4 }}>
          {NAV_LINKS.map((l) => (
            <a
              key={l.id}
              href={
                l.to.startsWith("/") && !l.to.includes("#") ? undefined : l.to
              }
              onClick={
                l.to.startsWith("/") && !l.to.includes("#")
                  ? undefined
                  : undefined
              }
              {...(l.to.startsWith("/") && !l.to.includes("#")
                ? { href: l.to }
                : { href: l.to })}
              style={{
                padding: "8px 14px",
                textDecoration: "none",
                fontFamily: "var(--fw-mono)",
                fontSize: 11,
                letterSpacing: "1.4px",
                textTransform: "uppercase",
                color: isActive(l) ? "var(--fw-ink)" : "var(--fw-muted)",
                borderBottom: isActive(l)
                  ? "1.5px solid var(--fw-red)"
                  : "1.5px solid transparent",
                transition: "color 0.15s",
              }}
              onMouseEnter={(e) => {
                if (!isActive(l)) e.currentTarget.style.color = "var(--fw-ink)";
              }}
              onMouseLeave={(e) => {
                if (!isActive(l))
                  e.currentTarget.style.color = "var(--fw-muted)";
              }}
            >
              {l.label}
            </a>
          ))}
        </div>

        {/* Auth */}
        <div
          className="fw-nav-auth"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 4,
            marginRight: 8,
          }}
        >
          {user ? (
            <>
              <Link to={dashboardPath} style={authLinkStyle}>
                Dashboard
              </Link>
              <button onClick={logout} style={authLinkStyle}>
                Sign out
              </button>
            </>
          ) : (
            <Link to="/login" style={authLinkStyle}>
              Sign in
            </Link>
          )}
        </div>

        {/* CTA */}
        <a
          href="/start"
          style={{
            background: "var(--fw-red)",
            color: "var(--fw-cream)",
            padding: "10px 18px",
            textDecoration: "none",
            fontFamily: "var(--fw-mono)",
            fontSize: 11,
            letterSpacing: "1.4px",
            textTransform: "uppercase",
            fontWeight: 500,
            whiteSpace: "nowrap",
            transition: "background 0.15s",
          }}
          onMouseEnter={(e) =>
            (e.currentTarget.style.background = "var(--fw-red-hover)")
          }
          onMouseLeave={(e) =>
            (e.currentTarget.style.background = "var(--fw-red)")
          }
          className="fw-nav-cta"
        >
          Start a project →
        </a>

        {/* Mobile menu toggle */}
        <button
          className="fw-nav-mobile-toggle"
          onClick={() => setMenuOpen((o) => !o)}
          style={{
            display: "none",
            background: "none",
            border: "none",
            cursor: "pointer",
            padding: 8,
            color: "var(--fw-ink)",
            fontFamily: "var(--fw-mono)",
            fontSize: 11,
            letterSpacing: "1.4px",
            textTransform: "uppercase",
          }}
        >
          {menuOpen ? "Close" : "Menu"}
        </button>
      </div>

      {/* Mobile drawer */}
      {menuOpen && (
        <div
          className="fw-nav-mobile-drawer"
          style={{
            borderTop: "1px solid var(--fw-line)",
            padding: "20px 40px 28px",
            display: "flex",
            flexDirection: "column",
            gap: 0,
            background: "var(--fw-cream)",
          }}
        >
          {NAV_LINKS.map((l) => (
            <a
              key={l.id}
              href={l.to}
              onClick={() => setMenuOpen(false)}
              style={{
                padding: "14px 0",
                borderBottom: "1px solid var(--fw-line)",
                textDecoration: "none",
                fontFamily: "var(--fw-mono)",
                fontSize: 13,
                letterSpacing: "1.4px",
                textTransform: "uppercase",
                color: isActive(l) ? "var(--fw-ink)" : "var(--fw-muted)",
              }}
            >
              {l.label}
            </a>
          ))}
          {user ? (
            <>
              <Link
                to={dashboardPath}
                onClick={() => setMenuOpen(false)}
                style={{
                  padding: "14px 0",
                  borderBottom: "1px solid var(--fw-line)",
                  textDecoration: "none",
                  fontFamily: "var(--fw-mono)",
                  fontSize: 13,
                  letterSpacing: "1.4px",
                  textTransform: "uppercase",
                  color: "var(--fw-muted)",
                }}
              >
                Dashboard
              </Link>
              <button
                onClick={() => {
                  logout();
                  setMenuOpen(false);
                }}
                style={{
                  padding: "14px 0",
                  borderBottom: "1px solid var(--fw-line)",
                  background: "none",
                  border: "none",
                  textAlign: "left",
                  cursor: "pointer",
                  fontFamily: "var(--fw-mono)",
                  fontSize: 13,
                  letterSpacing: "1.4px",
                  textTransform: "uppercase",
                  color: "var(--fw-muted)",
                }}
              >
                Sign out
              </button>
            </>
          ) : (
            <Link
              to="/login"
              onClick={() => setMenuOpen(false)}
              style={{
                padding: "14px 0",
                borderBottom: "1px solid var(--fw-line)",
                textDecoration: "none",
                fontFamily: "var(--fw-mono)",
                fontSize: 13,
                letterSpacing: "1.4px",
                textTransform: "uppercase",
                color: "var(--fw-muted)",
              }}
            >
              Sign in
            </Link>
          )}
          <a
            href="/start"
            onClick={() => setMenuOpen(false)}
            style={{
              marginTop: 20,
              background: "var(--fw-red)",
              color: "var(--fw-cream)",
              padding: "14px 20px",
              textDecoration: "none",
              textAlign: "center",
              fontFamily: "var(--fw-mono)",
              fontSize: 12,
              letterSpacing: "1.4px",
              textTransform: "uppercase",
            }}
          >
            Start a project →
          </a>
        </div>
      )}

      <style>{`
        @media (max-width: 768px) {
          .fw-nav-links { display: none !important; }
          .fw-nav-cta { display: none !important; }
          .fw-nav-auth { display: none !important; }
          .fw-nav-mobile-toggle { display: block !important; }
        }
      `}</style>
    </nav>
  );
};

export default Navigation;
